import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MultiLineChart from '../multi-line-chart/multi-line-chart';
import { DeviceModelType } from 'apps/marsinet/src/app/models/devicesModels';

interface RoleBasedMultiLineChartProps {
  role: string;
  data: any[];
  keyMapAdmin?: Record<string, string>;
  keyMapUser?: Record<string, string>;
  deviceModel: DeviceModelType | null;
  isAdminRole: (role: string) => boolean;
  titleAdmin: string;
  titleUser: string;
  [key: string]: any; // para otras props de MultiLineChart
}

function remapKeys(data: any[], keyMap: Record<string, string>) {
  return data.map((item) => {
    const newItem: Record<string, any> = {};
    Object.entries(item).forEach(([key, value]) => {
      if (key === 'timestamp') {
        newItem[key] = value;
      } else if (keyMap[key]) {
        newItem[keyMap[key]] = value;
      }
    });
    return newItem;
  });
}

const RoleBasedMultiLineChart: React.FC<RoleBasedMultiLineChartProps> = ({
  role,
  data,
  keyMapAdmin = {},
  keyMapUser = {},
  deviceModel,
  labelsAdmin,
  labelsUser,
  isAdminRole,
  titleAdmin,
  titleUser,
  setLoading,
  ...rest
}) => {
  const isAdmin = isAdminRole(role);
  const { t } = useTranslation();

  const userRefKeys = useMemo(
    () =>
      deviceModel === DeviceModelType.ATLAS
        ? [
          'motorsAngRef2', // CI
          'motorsAngRef3', // RI
          'motorsAngRef4',
          'motorsAngRef6', // CD
          'motorsAngRef7', // RD
          'motorsAngRef8',
        ]
        : [
          'motorsAngRef2', // CI
          'motorsAngRef3', // RI 
          'motorsAngRef6', // CD
          'motorsAngRef7', // RD
        ],
    [deviceModel]
  );
  // Diccionario que asocia cada clave de referencia con su valor Mille correspondiente
  const milleMap = {
    // motorsAngRef1: 'motorsMotor1Mille',
    motorsAngRef2: 'motorsMotor2Mille',
    motorsAngRef3: 'motorsMotor3Mille',
    motorsAngRef4: 'motorsMotor4Mille',
    // motorsAngRef5: 'motorsMotor5Mille',
    motorsAngRef6: 'motorsMotor6Mille',
    motorsAngRef7: 'motorsMotor7Mille',
    motorsAngRef8: 'motorsMotor8Mille',
  };

  const mappedData = useMemo(() => {
    //Si el usuario es Admin devuelve los datos tal cual
    if (isAdmin) return remapKeys(data, keyMapAdmin);
    // Para clínico: crea un objeto solo con las claves,
    //Si existe y es diferente de 0, se usa ese valor.
    // Si no, se usa el valor Mille correspondiente.
    // Si tampoco existe, se pone 0.
    return data.map((item) => {
      const newItem: Record<string, any> = { timestamp: item.timestamp };
      userRefKeys.forEach((refKey) => {
        if (item[refKey] !== undefined && item[refKey] !== 0) {
          newItem[refKey] = item[refKey];
        } else if (item[milleMap[refKey]] !== undefined) {
          newItem[refKey] = item[milleMap[refKey]];
        } else {
          newItem[refKey] = 0;
        }
      });
      return newItem;
    });
  }, [isAdmin, data, keyMapAdmin]);

  const userLegendLabels = useMemo(
    () => ({
      motorsAngRef2: t('pages.patients.viewPatient.sessions.romHipLeft'),
      motorsAngRef3: t('pages.patients.viewPatient.sessions.romKneeLeft'),
      motorsAngRef4: t('pages.patients.viewPatient.sessions.romAnkleLeft'),
      motorsAngRef6: t('pages.patients.viewPatient.sessions.romHipRight'),
      motorsAngRef7: t('pages.patients.viewPatient.sessions.romKneeRight'),
      motorsAngRef8: t('pages.patients.viewPatient.sessions.romAnkleRight'),
    }),
    [t]
  );

  return (
    <MultiLineChart
      data={mappedData}
      title={isAdmin ? titleAdmin : titleUser}
      setLoading={setLoading}
      legendKeyFilter={isAdmin ? undefined : userRefKeys}
      legendCustomLabels={isAdmin ? undefined : userLegendLabels}
      {...rest}
    />
  );
};
export default RoleBasedMultiLineChart;
